import * as DSAssetFamilies from 'customer-data-filters-ui/filterQueryFormat/DSAssetFamilies/DSAssetFamilies';
import { HUBSPOT_DEFINED, USER_DEFINED } from 'customer-data-filters-ui/filterQueryFormat/associations/AssociationCategory';
import I18n from 'I18n';
import { Map as ImmutableMap, OrderedMap } from 'immutable';
import DSFieldRecord from 'customer-data-filters-ui/filterQueryFormat/DSFieldRecord/DSFieldRecord';
import { ObjectTypeIdRegex, isAnyEventTypeId } from 'customer-data-filters-ui/filterQueryFormat/ObjectTypeId';
import { filterFamilySupportsDynamic, makeDynamicFilterFamily, parseDynamicFilterFamilyId, testDynamicFilterFamily } from 'customer-data-filters-ui/filterQueryFormat/DynamicFilterFamilies';
import { getSpecialField, isSpecialField } from 'customer-data-filters-ui/converters/listSegClassic/listSegFields';
import { getVisibleFields } from 'customer-data-filters-ui/converters/listSegClassic/listSegFields';
import FormattedMessage from 'I18n/components/FormattedMessage';
// @ts-expect-error ts-migrate(7016) FIXME: Could not find a declaration file for module 'cust... Remove this comment to see the full error message
import PropertyGroupRecord from 'customer-data-objects/property/PropertyGroupRecord';
import getIn from 'transmute/getIn';
import once from 'transmute/once';
import update from 'transmute/update';
import { makeAssociationOption } from './strategyUtils';
import { hasEventsForAllObjects } from '../permissions/gates';
import { CONTACT_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { jsx as _jsx } from "react/jsx-runtime";
export const getEventSupportsObjectTypeId = (event, objectTypeId) => {
  const supportedIds = event.get('requiredObjectTypeIds');

  // The old behavior was to use all events for contacts
  if (!hasEventsForAllObjects()) {
    return objectTypeId === CONTACT_TYPE_ID;
  }

  // Empty array means that event supports all object types
  if (supportedIds.length === 0) {
    return true;
  }
  return supportedIds.includes(objectTypeId);
};

/**
 * Extract association id and category from filter branch
 */
export const getAssociationSpecFromFilterBranch = filterBranch => {
  const {
    associationTypeId,
    associationCategory
  } = filterBranch;
  return {
    associationTypeId,
    associationCategory
  };
};

/**
 * Get an internal primary association between two objects.
 *
 * Association is considered primary if it has "ONE_TO_ONE" cardinality
 * or the lowest `id`
 * (see https://hubspot.slack.com/archives/C01B5SP6Y76/p1603380387131400)
 */
export const getInternalPrimaryAssociation = associationsBetweenObjects => {
  let primaryAssociation;
  for (const association of associationsBetweenObjects.values()) {
    if (association.cardinality === 'ONE_TO_ONE') {
      primaryAssociation = association;
      break;
    }
    if (!primaryAssociation || association.id < primaryAssociation.id) {
      primaryAssociation = association;
    }
  }
  return primaryAssociation;
};

/**
 * Get a wildcard association between two object types.
 *
 * Wildcard is an association that always exists between two
 * records of two different object types
 */
export const getWildcardAssociation = associationsBetweenObjects => {
  return associationsBetweenObjects.find(assn => assn.hasAllAssociatedObjects);
};

/**
 * Get association between two object types based on association id.
 */
export const getAssociationById = (associationsBetweenObjects, associationId, associationCategory) => {
  return associationsBetweenObjects.find(assn => assn.id === associationId && assn.category === associationCategory);
};

/**
 * Get association between two object types based on inverse association id.
 */
export const getAssociationByInverseId = (associationsBetweenObjects, associationId, associationCategory) => {
  return associationsBetweenObjects.find(assn => assn.inverseId === associationId && assn.category === associationCategory);
};

/**
 * Get a default association between two object types
 *
 * If provided object types have more then one association they should have
 * a wildcard association, if not use internal primary association
 */
export const getDefaultAssociation = (fromObjectTypeId, toObjectTypeId, associationDefinitions = ImmutableMap()) => {
  const associationsBetweenObjects = associationDefinitions.getIn([fromObjectTypeId, toObjectTypeId]);
  if (!associationsBetweenObjects) {
    return undefined;
  }
  return getWildcardAssociation(associationsBetweenObjects) || getInternalPrimaryAssociation(associationsBetweenObjects);
};
export const parseAssociationOption = assnOption => {
  const [id, category] = assnOption.split('--');
  return {
    id: parseInt(id, 10),
    category
  };
};
export const isAssociationBranch = group => {
  return typeof group.get('associationTypeId') === 'number' && [HUBSPOT_DEFINED, USER_DEFINED].includes(group.get('associationCategory'));
};
export const getAssociationValueFromFilterBranch = group => {
  if (!isAssociationBranch(group)) {
    return undefined;
  }
  return makeAssociationOption(group.get('associationTypeId'), group.get('associationCategory'));
};
export const getUnifiedEventsCategoryGroupFields = (categoryGroupName, props) => {
  const {
    data,
    hasScope
  } = props;
  const unifiedEvents = data.get(DSAssetFamilies.UNIFIED_EVENT);
  return unifiedEvents.filter(ue => {
    const hasAccess = ue.get('scopes').every(scope => hasScope(scope)) && ue.get('isUngated');
    return ue.categoryGroupName === categoryGroupName && hasAccess;
  }).map(ue => DSFieldRecord({
    name: ue.objectTypeId,
    label: ue.label
  }));
};
export const getCustomBehavioralEventGroupFields = (customBehavioralEvents = ImmutableMap()) => {
  return customBehavioralEvents.map(ue => DSFieldRecord({
    name: ue.objectTypeId,
    label: ue.label
  }));
};
const getFetchedFailedMessage = once(() => /*#__PURE__*/_jsx(FormattedMessage, {
  message: "customerDataFiltersUI.customerDataFilters.MissingField.fetchFailed"
}));
const getSpecialFieldGroups = assetFamily => {
  if (assetFamily === DSAssetFamilies.CONTACT_PROPERTY) {
    return OrderedMap({
      privacy: DSFieldRecord({
        label: I18n.text('customerDataFiltersUI.customerDataFilters.FilterPropertyGroupTranslator.privacy'),
        name: 'privacy',
        displayOrder: 100
      })
    });
  }
  return OrderedMap();
};
export const isFieldGrouped = assetFamily => {
  if (isAnyEventTypeId(assetFamily)) {
    return false;
  }
  return ObjectTypeIdRegex.test(assetFamily) || assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION;
};
export const getCommonField = (assetFamily, id, defaultField, props) => {
  const {
    data
  } = props;
  if (testDynamicFilterFamily(DSAssetFamilies.INTEGRATION, assetFamily)) {
    const integrations = data.get(DSAssetFamilies.INTEGRATION);
    const integrationId = parseDynamicFilterFamilyId(DSAssetFamilies.INTEGRATION, assetFamily);
    const field = isSpecialField(id, assetFamily) ? getSpecialField(id, assetFamily) : data.getIn([DSAssetFamilies.INTEGRATION_PROPERTY, integrationId, id]);
    if (integrations && field) {
      return integrations.has(integrationId) ? field : defaultField.set('errorMessage', getFetchedFailedMessage());
    }
    return defaultField;
  }
  if (filterFamilySupportsDynamic(assetFamily)) {
    return data.getIn([makeDynamicFilterFamily(assetFamily, props.baseFilterFamily), `${id}`]);
  }
  if (isSpecialField(id, assetFamily)) {
    return getSpecialField(id, assetFamily);
  }
  return undefined;
};
export const getFieldGroups = (assetFamily, props) => {
  const {
    data,
    hasScope
  } = props;
  if (isFieldGrouped(assetFamily)) {
    const fields = data.get(assetFamily);
    let groups = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? data.get(DSAssetFamilies.SURVEY_MONKEY_SURVEY) : data.getIn([DSAssetFamilies.PROPERTY_GROUP, assetFamily]);
    if (groups && fields) {
      groups = groups.merge(getSpecialFieldGroups(assetFamily));
      const scopes = {
        'crm-views-all': hasScope('crm-views-all')
      };
      const filteredFields = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? fields : getVisibleFields(fields, assetFamily, scopes);
      return filteredFields.reduce((acc, entry) => {
        const key = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? getIn(['metadata', 'surveyId'], entry) : getIn(['wrappedObject', 'groupName'], entry);
        const displayName = assetFamily === DSAssetFamilies.SURVEY_MONKEY_QUESTION ? groups.getIn([key, 'label']) : groups.getIn([key, 'label']) || groups.getIn([key, 'name']);
        const fieldGroup = PropertyGroupRecord({
          displayOrder: groups.getIn([key, 'displayOrder']),
          displayName,
          name: groups.getIn([key, 'name'])
        });
        return update(
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        key, (group = fieldGroup) => update('properties', properties => properties.push(entry),
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        group), acc);
      }, OrderedMap()).filter(propertyGroupRecord => propertyGroupRecord.displayName).sort((a, b) => a.displayOrder - b.displayOrder);
    }
  }
  return undefined;
};

// Strategies must support the PropertyFilter component,
//   which is used to edit a single filter condition.
// Ideally, strategies would implement a serializer for a single filter condition,
//   but the current architecture makes that difficult:
//   https://git.hubteam.com/HubSpot/customer-data-filters/pull/5235#discussion_r3865681
// These functions wrap/unwrap a single filter condition from a filter query.
export const wrapSingleILSFilter = singleFilter => {
  return {
    filterBranchType: 'OR',
    filterBranchOperator: 'OR',
    filters: [],
    filterBranches: [{
      filterBranchType: 'AND',
      filterBranchOperator: 'AND',
      filters: singleFilter ? [singleFilter] : [],
      filterBranches: []
    }]
  };
};
export const unwrapSingleILSFilter = filterQuery => {
  var _filterQuery$filterBr;
  return filterQuery === null || filterQuery === void 0 || (_filterQuery$filterBr = filterQuery.filterBranches) === null || _filterQuery$filterBr === void 0 || (_filterQuery$filterBr = _filterQuery$filterBr[0]) === null || _filterQuery$filterBr === void 0 || (_filterQuery$filterBr = _filterQuery$filterBr.filters) === null || _filterQuery$filterBr === void 0 ? void 0 : _filterQuery$filterBr[0];
};